/**
 * API Client with Streaming Support and Date Conversion
 *
 * This demonstrates how to use the generated types with the conversion utilities
 * for seamless frontend-backend communication, including streaming responses and
 * automatic date field conversion.
 */

// Import generated types (from running generate_types.py)
import * as Types from 'types/types';
import {
  formatApiRequest,
  parseApiResponse,
  parsePaginatedResponse,
  handleApiResponse,
  // handlePaginatedApiResponse,
  createPaginatedRequest,
  toUrlParams,
  extractApiData,
  // ApiResponse,
  PaginatedResponse,
  PaginatedRequest,
  toSnakeCase,
} from 'types/conversion';

// Import generated date conversion functions
import { convertFromApi, convertArrayFromApi } from 'types/types';

import {
  validatePasswordStrength as validatePasswordStrengthRules,
  validateEmail as validateEmailRule,
  validateUsername as validateUsernameRule,
} from '../domain/auth-rules';

const TOKEN_STORAGE = {
  ACCESS_TOKEN: 'accessToken',
  REFRESH_TOKEN: 'refreshToken',
  USER_DATA: 'userData',
  TOKEN_EXPIRY: 'tokenExpiry',
  USER_TYPE: 'userType',
  IS_GUEST: 'isGuest',
  PENDING_VERIFICATION_EMAIL: 'pendingVerificationEmail',
} as const;

const isFormData = (body: unknown): body is FormData => {
  if (!body) {
    return false;
  }
  if (typeof FormData !== 'undefined' && body instanceof FormData) {
    return true;
  }
  if (Object.prototype.toString.call(body) === '[object FormData]') {
    return true;
  }
  const candidate = body as FormData;
  return (
    typeof candidate.append === 'function' &&
    (typeof candidate.get === 'function' || typeof candidate.forEach === 'function')
  );
};

// ============================
// Error Handling Types
// ============================

export interface ApiErrorDetails {
  status: number;
  statusText: string;
  url: string;
  method: string;
  errorBody?: any;
  headers?: Record<string, string>;
  timestamp: Date;
}

export type ApiErrorCallback = (error: ApiErrorDetails) => void | Promise<void>;

// ============================
// Streaming Types and Interfaces
// ============================
export interface GuestConversionRequest extends CreateCandidateRequest {
  accountType: 'candidate';
}

export class RateLimitError extends Error {
  constructor(
    message: string,
    public retryAfterSeconds: number,
    public remainingRequests: Record<string, number>
  ) {
    super(message);
    this.name = 'RateLimitError';
  }
}
interface StreamingOptions<T = Types.ChatMessage> {
  method?: string;
  headers?: Record<string, any>;
  onStatus?: (status: Types.ChatMessageStatus) => void;
  onMessage?: (message: T) => void;
  onStreaming?: (chunk: Types.ChatMessageStreaming) => void;
  onComplete?: () => void;
  onError?: (error: Types.ChatMessageError) => void;
  onWarn?: (warning: string) => void;
  onSlowStart?: (message: string) => void;
  onUnavailable?: (message: string) => void;
  signal?: AbortSignal;
}

type StreamingFeedbackCallbacks = Pick<StreamingOptions, 'onSlowStart' | 'onUnavailable'>;

const DEFAULT_STREAM_SLOW_START_MS = 4000;
const STREAM_SLOW_START_REPEAT_MS = 10000;
const AI_SERVICE_UNAVAILABLE_MESSAGE = 'The AI service is unavailable. Please retry later.';
const STREAM_SLOW_START_MESSAGES = [
  'Still thinking... please be patient.',
  "Still thinking... I'm sorry for the wait.",
  'Still thinking... the NVIDIA GB10 is in the mail.',
  'Still thinking... warming up the tensor cores.',
  'Still thinking... asking the model to take a deep breath.',
  'Still thinking... the answer is taking the scenic route.',
  'Still thinking... checking the fine print twice.',
  'Still thinking... gathering the good bits.',
  'Still thinking... patience is being converted into tokens.',
  'Still thinking... negotiating with latency.',
  'Still thinking... the model is sharpening its pencil.',
  'Still thinking... searching for the least hand-wavy answer.',
  'Still thinking... coaxing the response out gently.',
  'Still thinking... waiting for the first token to arrive.',
  'Still thinking... making sure this is worth the wait.',
  'Still thinking... the silicon is doing its best.',
  'Still thinking... checking one more angle.',
  'Still thinking... letting the context settle.',
  'Still thinking... the response is buffering its courage.',
  'Still thinking... consulting the attention heads.',
  'Still thinking... turning compute into clarity.',
  'Still thinking... there is a tiny queue at the idea desk.',
  'Still thinking... calibrating the confidence meter.',
  'Still thinking... keeping the answer out of the ditch.',
  'Still thinking... waiting on a very thoughtful token.',
  'Still thinking... aligning the thoughts in semantic order.',
  'Still thinking... giving the model a moment to cook.',
  'Still thinking... composing something useful.',
  'Still thinking... trying not to hallucinate under pressure.',
  'Still thinking... the response is almost certainly not ignoring you.',
];

interface DeleteResponse {
  success: boolean;
  message: string;
}

interface StreamingResponse<T = Types.ChatMessage> {
  messageId: string;
  cancel: () => void;
  promise: Promise<T>;
}

interface CreateCandidateAIResponse {
  message: string;
  candidate: Types.CandidateAI;
  resume: string;
}

export interface PasswordResetRequest {
  email: string;
}

export interface PasswordResetConfirm {
  token: string;
  newPassword: string;
}

// ============================
// Chat Types and Interfaces
// ============================

export interface CandidateInfo {
  id: string;
  name: string;
  email: string;
  username: string;
  skills: string[];
  experience: number;
  location: string;
}

export interface CreateChatSessionRequest {
  username?: string; // Optional candidate username to associate with
  context: Types.ChatContext;
  title?: string;
}

export interface UpdateChatSessionRequest {
  title?: string;
  context?: Partial<Types.ChatContext>;
  isArchived?: boolean;
  systemPrompt?: string;
}

export interface CandidateSessionsResponse {
  candidate: {
    id: string;
    username: string;
    fullName: string;
    email: string;
  };
  sessions: PaginatedResponse<Types.ChatSession>;
}

// ============================
// API Client Class
// ============================

class ApiClient {
  private baseUrl: string;
  private defaultHeaders: Record<string, string>;
  private errorCallback?: ApiErrorCallback;
  private streamingFeedbackCallbacks: StreamingFeedbackCallbacks = {};

  private stripContentType(headers: Record<string, string>): Record<string, string> {
    const cleaned = { ...headers };
    delete cleaned['Content-Type'];
    delete cleaned['content-type'];
    return cleaned;
  }

  private normalizeHeaders(headers?: HeadersInit): Record<string, string> {
    if (!headers) {
      return {};
    }
    if (headers instanceof Headers) {
      return Object.fromEntries(headers.entries());
    }
    if (Array.isArray(headers)) {
      return Object.fromEntries(headers);
    }
    return { ...headers };
  }

  constructor(accessToken?: string) {
    const loc = window.location;
    if (!loc.host.match(/.*battle-linux.*/)) {
      this.baseUrl = loc.protocol + '//' + loc.host + '/api/1.0';
    } else {
      this.baseUrl = loc.protocol + '//battle-linux.ketrenos.com:8912/api/1.0';
    }
    this.defaultHeaders = {
      'Content-Type': 'application/json',
      ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
    };
  }

  // ============================
  // Error Handling Methods
  // ============================

  /**
   * Set the error callback for handling API errors
   */
  setErrorCallback(callback: ApiErrorCallback): void {
    this.errorCallback = callback;
  }

  /**
   * Clear the registered error callback
   */
  clearErrorCallback(): void {
    this.errorCallback = undefined;
  }

  /**
   * Set default callbacks for streaming AI feedback.
   */
  setStreamingFeedbackCallbacks(callbacks: StreamingFeedbackCallbacks): void {
    this.streamingFeedbackCallbacks = callbacks;
  }

  /**
   * Clear default callbacks for streaming AI feedback.
   */
  clearStreamingFeedbackCallbacks(): void {
    this.streamingFeedbackCallbacks = {};
  }

  /**
   * Centralized fetch wrapper with error handling
   */
  private async fetchWithErrorHandling(
    url: string,
    options: RequestInit & { skipDefaultHeaders?: boolean; suppressErrorCallback?: boolean } = {}
  ): Promise<Response> {
    const fullUrl = url.startsWith('http') ? url : `${this.baseUrl}${url}`;
    const method = options.method || 'GET';
    const skipDefaultHeaders = options.skipDefaultHeaders === true;
    const suppressErrorCallback = options.suppressErrorCallback === true;

    try {
      const incomingHeaders = this.normalizeHeaders(options.headers);
      const mergedHeaders: Record<string, string> = skipDefaultHeaders
        ? { ...incomingHeaders }
        : {
            ...this.defaultHeaders,
            ...incomingHeaders,
          };
      if (isFormData(options.body)) {
        // Let the browser set the multipart boundary.
        delete mergedHeaders['Content-Type'];
        delete mergedHeaders['content-type'];
      }
      const response = await fetch(fullUrl, {
        ...options,
        headers: mergedHeaders,
      });

      // Handle error responses
      if (!response.ok) {
        const errorDetails: ApiErrorDetails = {
          status: response.status,
          statusText: response.statusText,
          url: fullUrl,
          method,
          timestamp: new Date(),
        };

        // Try to extract error body
        try {
          const contentType = response.headers.get('content-type');
          if (contentType?.includes('application/json')) {
            errorDetails.errorBody = await response.clone().json();
          } else {
            errorDetails.errorBody = await response.clone().text();
          }
        } catch {
          // Ignore errors when parsing error body
        }

        // Extract relevant response headers
        errorDetails.headers = {};
        response.headers.forEach((value, key) => {
          if (errorDetails.headers) {
            errorDetails.headers[key] = value;
          }
        });

        // Call the error callback if registered
        if (this.errorCallback && !suppressErrorCallback) {
          try {
            await this.errorCallback(errorDetails);
          } catch (callbackError) {
            console.error('Error callback failed:', callbackError);
          }
        }

        // Handle specific error types
        if (response.status === 429) {
          const rateLimitData = errorDetails.errorBody;
          const retryAfter = response.headers.get('Retry-After');

          throw new RateLimitError(
            rateLimitData?.detail?.message || 'Rate limit exceeded',
            parseInt(retryAfter || '60'),
            rateLimitData?.detail?.remaining || {}
          );
        }

        // Return the response for existing error handling to process
        return response;
      }

      return response;
    } catch (error) {
      // Handle network errors or other fetch failures
      if (error instanceof RateLimitError) {
        throw error; // Re-throw rate limit errors
      }

      const errorDetails: ApiErrorDetails = {
        status: 0,
        statusText: 'Network Error',
        url: fullUrl,
        method,
        errorBody: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date(),
      };

      // Call the error callback for network errors too
      if (this.errorCallback && !suppressErrorCallback) {
        try {
          await this.errorCallback(errorDetails);
        } catch (callbackError) {
          console.error('Error callback failed:', callbackError);
        }
      }

      throw error;
    }
  }

  // ============================
  // Response Handlers with Date Conversion
  // ============================

  /**
   * Handle API response with automatic date conversion for specific model types
   */
  private async handleApiResponseWithConversion<T>(
    response: Response,
    modelType?: string
  ): Promise<T> {
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const data = await response.json();
    const apiResponse = parseApiResponse<T>(data);
    const extractedData = extractApiData(apiResponse);

    // Apply model-specific date conversion if modelType is provided
    if (modelType) {
      return convertFromApi<T>(extractedData, modelType);
    }

    return extractedData;
  }

  /**
   * Handle paginated API response with automatic date conversion
   */
  private async handlePaginatedApiResponseWithConversion<T>(
    response: Response,
    modelType?: string
  ): Promise<PaginatedResponse<T>> {
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const data = await response.json();
    const apiResponse = parsePaginatedResponse<T>(data);
    const extractedData = extractApiData(apiResponse);
    // Apply model-specific date conversion to array items if modelType is provided
    if (modelType && extractedData.data) {
      return {
        ...extractedData,
        data: convertArrayFromApi<T>(extractedData.data, modelType),
      };
    }

    return extractedData;
  }

  /**
   * Create candidate with email verification
   */
  async createCandidate(candidate: CreateCandidateRequest): Promise<RegistrationResponse> {
    const response = await this.fetchWithErrorHandling('/candidates', {
      method: 'POST',
      body: JSON.stringify(formatApiRequest(candidate)),
    });

    return handleApiResponse<RegistrationResponse>(response);
  }

  async createCandidateAI(userMessage: Types.ChatMessageUser): Promise<CreateCandidateAIResponse> {
    const response = await this.fetchWithErrorHandling('/candidates/ai', {
      method: 'POST',
      body: JSON.stringify(formatApiRequest(userMessage)),
    });

    const result = await handleApiResponse<CreateCandidateAIResponse>(response);
    return {
      message: result.message,
      candidate: convertFromApi<Types.CandidateAI>(result.candidate, 'CandidateAI'),
      resume: result.resume,
    };
  }

  /**
   * Create employer with email verification
   */
  async createEmployerWithVerification(
    employer: CreateEmployerRequest
  ): Promise<RegistrationResponse> {
    const response = await this.fetchWithErrorHandling('/employers', {
      method: 'POST',
      body: JSON.stringify(formatApiRequest(employer)),
    });

    return handleApiResponse<RegistrationResponse>(response);
  }

  /**
   * Verify email address
   */
  async verifyEmail(request: EmailVerificationRequest): Promise<EmailVerificationResponse> {
    const response = await this.fetchWithErrorHandling('/auth/verify-email', {
      method: 'POST',
      body: JSON.stringify(formatApiRequest(request)),
    });

    return handleApiResponse<EmailVerificationResponse>(response);
  }

  /**
   * Resend verification email
   */
  async resendVerificationEmail(request: ResendVerificationRequest): Promise<{ message: string }> {
    const response = await this.fetchWithErrorHandling('/auth/resend-verification', {
      method: 'POST',
      body: JSON.stringify(formatApiRequest(request)),
    });

    return handleApiResponse<{ message: string }>(response);
  }

  /**
   * Request MFA for new device
   */
  async requestMFA(request: MFARequest): Promise<Types.MFARequestResponse> {
    const response = await this.fetchWithErrorHandling('/auth/mfa/request', {
      method: 'POST',
      body: JSON.stringify(formatApiRequest(request)),
    });

    return handleApiResponse<Types.MFARequestResponse>(response);
  }

  /**
   * Verify MFA code
   */
  async verifyMFA(request: Types.MFAVerifyRequest): Promise<Types.AuthResponse> {
    const formattedRequest = formatApiRequest(request);
    const response = await this.fetchWithErrorHandling('/auth/mfa/verify', {
      method: 'POST',
      body: JSON.stringify(formattedRequest),
    });

    return handleApiResponse<Types.AuthResponse>(response);
  }

  /**
   * login with device detection
   */
  async login(auth: Types.LoginRequest): Promise<Types.AuthResponse | Types.MFARequestResponse> {
    const response = await this.fetchWithErrorHandling('/auth/login', {
      method: 'POST',
      body: JSON.stringify(formatApiRequest(auth)),
      suppressErrorCallback: true,
    });

    return handleApiResponse<Types.AuthResponse | Types.MFARequestResponse>(response);
  }

  /**
   * Logout with token revocation
   */
  async logout(
    accessToken: string,
    refreshToken: string
  ): Promise<{ message: string; tokensRevoked: any }> {
    const response = await this.fetchWithErrorHandling('/auth/logout', {
      method: 'POST',
      body: JSON.stringify(
        formatApiRequest({
          accessToken,
          refreshToken,
        })
      ),
    });

    return handleApiResponse<{ message: string; tokensRevoked: any }>(response);
  }

  /**
   * Logout from all devices
   */
  async logoutAllDevices(): Promise<{ message: string }> {
    const response = await this.fetchWithErrorHandling('/auth/logout-all', {
      method: 'POST',
    });

    return handleApiResponse<{ message: string }>(response);
  }

  // ============================
  // Device Management Methods
  // ============================

  /**
   * Get trusted devices for current user
   */
  async getTrustedDevices(): Promise<TrustedDevice[]> {
    const response = await this.fetchWithErrorHandling('/auth/trusted-devices');

    return handleApiResponse<TrustedDevice[]>(response);
  }

  /**
   * Remove trusted device
   */
  async removeTrustedDevice(deviceId: string): Promise<{ message: string }> {
    const response = await this.fetchWithErrorHandling(`/auth/trusted-devices/${deviceId}`, {
      method: 'DELETE',
    });

    return handleApiResponse<{ message: string }>(response);
  }

  /**
   * Get security log for current user
   */
  async getSecurityLog(days = 7): Promise<SecurityEvent[]> {
    const response = await this.fetchWithErrorHandling(`/auth/security-log?days=${days}`);

    return handleApiResponse<SecurityEvent[]>(response);
  }

  // ============================
  // Admin Methods (if user has admin role)
  // ============================

  /**
   * Get pending user verifications (admin only)
   */
  async getPendingVerifications(): Promise<PendingVerification[]> {
    const response = await this.fetchWithErrorHandling('/admin/pending-verifications');

    return handleApiResponse<PendingVerification[]>(response);
  }

  /**
   * Manually verify user (admin only)
   */
  async manuallyVerifyUser(userId: string, reason: string): Promise<{ message: string }> {
    const response = await this.fetchWithErrorHandling(`/admin/verify-user/${userId}`, {
      method: 'POST',
      body: JSON.stringify(formatApiRequest({ reason })),
    });

    return handleApiResponse<{ message: string }>(response);
  }

  /**
   * Get user security events (admin only)
   */
  async getUserSecurityEvents(userId: string, days = 30): Promise<SecurityEvent[]> {
    const response = await this.fetchWithErrorHandling(
      `/admin/users/${userId}/security-events?days=${days}`
    );

    return handleApiResponse<SecurityEvent[]>(response);
  }

  /**
   * Admin: fetch dashboard overview metrics
   */
  async getAdminOverview(): Promise<any> {
    const response = await this.fetchWithErrorHandling('/admin/metrics/overview');
    return handleApiResponse<any>(response);
  }

  /**
   * Admin: recent user access events
   */
  async getRecentUserAccess(limit = 50): Promise<any> {
    const response = await this.fetchWithErrorHandling(`/admin/metrics/recent-user-access?limit=${limit}`);
    return handleApiResponse<any>(response);
  }

  /**
   * Admin: recent chat sessions
   */
  async getRecentChatSessions(limit = 50): Promise<any> {
    const response = await this.fetchWithErrorHandling(`/admin/metrics/recent-chats?limit=${limit}`);
    return handleApiResponse<any>(response);
  }

  /**
   * Admin: set or unset admin flag for a user
   */
  async setUserAdmin(userId: string, isAdmin: boolean): Promise<any> {
    const response = await this.fetchWithErrorHandling(`/admin/users/${userId}/set-admin`, {
      method: 'POST',
      body: JSON.stringify(formatApiRequest({ isAdmin })),
    });

    return handleApiResponse<any>(response);
  }

  // ============================
  // Utility Methods
  // ============================

  /**
   * Generate device fingerprint for MFA
   */
  generateDeviceFingerprint(): string {
    // Create a basic device fingerprint
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillText('Device fingerprint', 2, 2);
    }

    const fingerprint =
      (canvas.toDataURL() || '') +
      navigator.userAgent +
      navigator.language +
      // screen.width + 'x' + screen.height +
      // (navigator.platform || '') +
      (navigator.cookieEnabled ? '1' : '0');

    // Create a hash-like string from the fingerprint
    let hash = 0;
    for (let i = 0; i < fingerprint.length; i++) {
      const char = fingerprint.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32bit integer
    }

    return Math.abs(hash).toString(16).slice(0, 16);
  }

  /**
   * Get device name from user agent
   */
  getDeviceName(): string {
    const ua = navigator.userAgent;

    const browser = ua.includes('Chrome')
      ? 'Chrome'
      : ua.includes('Firefox')
      ? 'Firefox'
      : ua.includes('Safari')
      ? 'Safari'
      : ua.includes('Edge')
      ? 'Edge'
      : 'Browser';

    const os = ua.includes('Windows')
      ? 'Windows'
      : ua.includes('Mac')
      ? 'macOS'
      : ua.includes('Linux')
      ? 'Linux'
      : ua.includes('Android')
      ? 'Android'
      : ua.includes('iOS')
      ? 'iOS'
      : 'Unknown OS';

    return `${browser} on ${os}`;
  }

  /**
   * Check if email verification is pending
   */
  isEmailVerificationPending(): boolean {
    return localStorage.getItem('pendingEmailVerification') === 'true';
  }

  /**
   * Set email verification pending status
   */
  setPendingEmailVerification(email: string, pending = true): void {
    if (pending) {
      localStorage.setItem('pendingEmailVerification', 'true');
      localStorage.setItem('pendingVerificationEmail', email);
    } else {
      localStorage.removeItem('pendingEmailVerification');
      localStorage.removeItem('pendingVerificationEmail');
    }
  }

  /**
   * Get pending verification email
   */
  getPendingVerificationEmail(): string | null {
    return localStorage.getItem('pendingVerificationEmail');
  }

  // ============================
  // Authentication Methods
  // ============================
  async refreshToken(refreshToken: string): Promise<Types.AuthResponse> {
    const response = await this.fetchWithErrorHandling('/auth/refresh', {
      method: 'POST',
      body: JSON.stringify(formatApiRequest({ refreshToken })),
      suppressErrorCallback: true,
    });

    return handleApiResponse<Types.AuthResponse>(response);
  }

  // ============================
  // Candidate Methods with Date Conversion
  // ============================

  // reference can be candidateId, username, or email
  async getCandidate(reference: string): Promise<Types.Candidate> {
    const response = await this.fetchWithErrorHandling(`/candidates/${reference}`);

    return this.handleApiResponseWithConversion<Types.Candidate>(response, 'Candidate');
  }

  async getJobAnalysis(job: Types.Job, candidate: Types.Candidate): Promise<Types.JobAnalysis> {
    const data: Types.JobAnalysis = {
      jobId: job.id || '',
      candidateId: candidate.id || '',
      skills: [],
    };
    const request = formatApiRequest(data);
    const response = await this.fetchWithErrorHandling('/candidates/job-analysis', {
      method: 'POST',
      body: JSON.stringify(request),
    });

    return this.handleApiResponseWithConversion<Types.JobAnalysis>(response, 'JobAnalysis');
  }

  async updateCandidate(id: string, updates: Partial<Types.Candidate>): Promise<Types.Candidate> {
    const request = formatApiRequest(updates);
    const response = await this.fetchWithErrorHandling(`/candidates/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(request),
    });

    return this.handleApiResponseWithConversion<Types.Candidate>(response, 'Candidate');
  }

  async deleteCandidate(id: string): Promise<DeleteResponse> {
    const response = await this.fetchWithErrorHandling(`/candidates/${id}`, {
      method: 'DELETE',
      body: JSON.stringify({ id }),
    });

    return handleApiResponse<DeleteResponse>(response);
  }

  async deleteJob(id: string): Promise<DeleteResponse> {
    const response = await this.fetchWithErrorHandling(`/jobs/${id}`, {
      method: 'DELETE',
      body: JSON.stringify({ id }),
    });

    return handleApiResponse<DeleteResponse>(response);
  }

  regenerateJob(
    job: Types.Job,
    streamingOptions?: StreamingOptions<Types.JobRequirementsMessage>
  ): StreamingResponse<Types.JobRequirementsMessage> {
    const body = JSON.stringify(formatApiRequest(job));
    return this.streamify<Types.JobRequirementsMessage>(
      '/jobs/regenerate',
      body,
      streamingOptions,
      'Job'
    );
  }

  async uploadCandidateProfile(file: File): Promise<boolean> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('filename', file.name);

    const response = await this.fetchWithErrorHandling('/candidates/profile/upload', {
      method: 'POST',
      headers: {
        // Don't set Content-Type - browser will set it automatically with boundary
        Authorization: this.defaultHeaders['Authorization'],
      },
      body: formData,
    });

    const result = await handleApiResponse<boolean>(response);

    return result;
  }

  async getCandidates(
    request: Partial<PaginatedRequest> = {}
  ): Promise<PaginatedResponse<Types.Candidate>> {
    const paginatedRequest = createPaginatedRequest(request);
    const params = toUrlParams(formatApiRequest(paginatedRequest));

    const response = await this.fetchWithErrorHandling(`/candidates?${params}`);

    return this.handlePaginatedApiResponseWithConversion<Types.Candidate>(response, 'Candidate');
  }

  async searchCandidates(
    query: string,
    filters?: Record<string, any>
  ): Promise<PaginatedResponse<Types.Candidate>> {
    const searchRequest = {
      query,
      filters,
      page: 1,
      limit: 20,
    };

    const params = toUrlParams(formatApiRequest(searchRequest));
    const response = await this.fetchWithErrorHandling(`/candidates/search?${params}`);

    return this.handlePaginatedApiResponseWithConversion<Types.Candidate>(response, 'Candidate');
  }

  // ============================
  // Employer Methods with Date Conversion
  // ============================

  async createEmployer(request: CreateEmployerRequest): Promise<Types.Employer> {
    const response = await this.fetchWithErrorHandling('/employers', {
      method: 'POST',
      body: JSON.stringify(formatApiRequest(request)),
    });

    return this.handleApiResponseWithConversion<Types.Employer>(response, 'Employer');
  }

  async getEmployer(id: string): Promise<Types.Employer> {
    const response = await this.fetchWithErrorHandling(`/employers/${id}`);

    return this.handleApiResponseWithConversion<Types.Employer>(response, 'Employer');
  }

  async updateEmployer(id: string, updates: Partial<Types.Employer>): Promise<Types.Employer> {
    const response = await this.fetchWithErrorHandling(`/employers/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(formatApiRequest(updates)),
    });

    return this.handleApiResponseWithConversion<Types.Employer>(response, 'Employer');
  }

  // ============================
  // Job Methods with Date Conversion
  // ============================
  async updateJob(id: string, updates: Partial<Types.Job>): Promise<Types.Job> {
    const response = await this.fetchWithErrorHandling(`/jobs/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(formatApiRequest(updates)),
    });

    return this.handleApiResponseWithConversion<Types.Job>(response, 'Job');
  }

  createJobFromDescription(
    job_description: string,
    streamingOptions?: StreamingOptions<Types.JobRequirementsMessage>
  ): StreamingResponse<Types.JobRequirementsMessage> {
    const body = JSON.stringify(job_description);
    return this.streamify<Types.JobRequirementsMessage>(
      '/jobs/from-content',
      body,
      streamingOptions,
      'JobRequirementsMessage'
    );
  }

  async createJob(
    job: Omit<Types.Job, 'id' | 'datePosted' | 'views' | 'applicationCount'>
  ): Promise<Types.Job> {
    const body = JSON.stringify(formatApiRequest(job));
    const response = await this.fetchWithErrorHandling('/jobs', {
      method: 'POST',
      body: body,
    });

    return this.handleApiResponseWithConversion<Types.Job>(response, 'Job');
  }

  saveResume(
    resume: Types.Resume,
    streamingOptions?: StreamingOptions<Types.ResumeMessage>
  ): StreamingResponse<Types.ResumeMessage> {
    const body = JSON.stringify(formatApiRequest(resume));
    return this.streamify<Types.ResumeMessage>(`/resumes`, body, streamingOptions, 'Resume');
  }

  // Additional API methods for Resume management
  async getResumes(): Promise<Types.Resume[]> {
    const response = await this.fetchWithErrorHandling('/resumes');

    const result = await handleApiResponse<{
      success: boolean;
      resumes: Types.Resume[];
    }>(response);

    // Convert all history items with proper date handling
    return convertArrayFromApi<Types.Resume>(result.resumes, 'Resume');
  }

  async getResume(resumeId: string): Promise<Types.Resume> {
    const response = await this.fetchWithErrorHandling(`/resumes/${resumeId}`);

    return this.handleApiResponseWithConversion<Types.Resume>(response, 'Resume');
  }

  async deleteResume(resumeId: string): Promise<{ success: boolean; message: string }> {
    const response = await this.fetchWithErrorHandling(`/resumes/${resumeId}`, {
      method: 'DELETE',
    });

    return handleApiResponse<{ success: boolean; message: string }>(response);
  }

  async getResumesByCandidate(candidateId: string, jobId?: string): Promise<Types.Resume[]> {
    let options = candidateId;
    if (jobId) {
      options += `/${jobId}`;
    }
    const response = await this.fetchWithErrorHandling(`/resumes/candidate/${options}`);
    const result = await handleApiResponse<{
      success: boolean;
      resumes: Types.Resume[];
    }>(response);

    // Convert all history items with proper date handling
    return convertArrayFromApi<Types.Resume>(result.resumes, 'Resume');
  }

  async getResumesByJob(jobId: string): Promise<Types.Resume[]> {
    const response = await this.fetchWithErrorHandling(`/resumes/job/${jobId}`);

    const result = await handleApiResponse<{
      success: boolean;
      resumes: Types.Resume[];
    }>(response);

    // Convert all history items with proper date handling
    return convertArrayFromApi<Types.Resume>(result.resumes, 'Resume');
  }

  async searchResumes(query: string): Promise<{
    success: boolean;
    query: string;
    resumes: Types.Resume[];
    count: number;
  }> {
    const params = new URLSearchParams({ q: query });
    const response = await this.fetchWithErrorHandling(`/resumes/search?${params}`);

    return handleApiResponse<{
      success: boolean;
      query: string;
      resumes: Types.Resume[];
      count: number;
    }>(response);
  }

  async getResumeStatistics(): Promise<{ success: boolean; statistics: any }> {
    const response = await this.fetchWithErrorHandling('/resumes/stats');

    return handleApiResponse<{ success: boolean; statistics: any }>(response);
  }

  async updateResume(resume: Types.Resume): Promise<Types.Resume> {
    const body = JSON.stringify(formatApiRequest(resume));
    const response = await this.fetchWithErrorHandling('/resumes', {
      method: 'PATCH',
      body: body,
    });

    return this.handleApiResponseWithConversion<Types.Resume>(response, 'Resume');
  }

  // ============================
  // Resume Revision History Methods
  // ============================

  /**
   * Get list of all revisions for a resume with metadata
   */
  async getResumeRevisions(resumeId: string): Promise<{
    success: boolean;
    resumeId: string;
    revisions: Array<{
      revisionId: string;
      revisionTimestamp: string;
      updatedAt: string;
      createdAt: string;
      candidateId: string;
      jobId: string;
    }>;
    count: number;
  }> {
    const response = await this.fetchWithErrorHandling(`/resumes/${resumeId}/revisions`);

    return handleApiResponse<{
      success: boolean;
      resumeId: string;
      revisions: Array<{
        revisionId: string;
        revisionTimestamp: string;
        updatedAt: string;
        createdAt: string;
        candidateId: string;
        jobId: string;
      }>;
      count: number;
    }>(response);
  }

  /**
   * Get a specific revision of a resume by revision ID
   */
  async getResumeRevision(
    resumeId: string,
    revisionId: string
  ): Promise<{
    success: boolean;
    resumeId: string;
    revisionId: string;
    revision: Types.Resume;
  }> {
    const response = await this.fetchWithErrorHandling(
      `/resumes/${resumeId}/revisions/${revisionId}`
    );

    const result = await handleApiResponse<{
      success: boolean;
      resumeId: string;
      revisionId: string;
      revision: any;
    }>(response);

    // Convert the revision data with proper date handling
    return {
      ...result,
      revision: convertFromApi<Types.Resume>(result.revision, 'Resume'),
    };
  }

  /**
   * Restore a resume to a specific revision
   */
  async restoreResumeRevision(
    resumeId: string,
    revisionId: string
  ): Promise<{
    success: boolean;
    resumeId: string;
    restoredFrom: string;
    resume: Types.Resume;
    message: string;
  }> {
    const response = await this.fetchWithErrorHandling(
      `/resumes/${resumeId}/restore/${revisionId}`,
      {
        method: 'POST',
      }
    );

    const result = await handleApiResponse<{
      success: boolean;
      resumeId: string;
      restoredFrom: string;
      resume: any;
      message: string;
    }>(response);

    // Convert the restored resume data with proper date handling
    return {
      ...result,
      resume: convertFromApi<Types.Resume>(result.resume, 'Resume'),
    };
  }

  /**
   * Delete a specific revision from history
   */
  async deleteResumeRevision(
    resumeId: string,
    revisionId: string
  ): Promise<{
    success: boolean;
    resumeId: string;
    revisionId: string;
    message: string;
  }> {
    const response = await this.fetchWithErrorHandling(
      `/resumes/${resumeId}/revisions/${revisionId}`,
      {
        method: 'DELETE',
      }
    );

    return handleApiResponse<{
      success: boolean;
      resumeId: string;
      revisionId: string;
      message: string;
    }>(response);
  }

  /**
   * Compare two revisions of a resume
   */
  async compareResumeRevisions(
    resumeId: string,
    revisionId1: string,
    revisionId2: string
  ): Promise<{
    success: boolean;
    resumeId: string;
    comparison: {
      revision1: {
        revisionId: string;
        data: Types.Resume;
      };
      revision2: {
        revisionId: string;
        data: Types.Resume;
      };
    };
  }> {
    const response = await this.fetchWithErrorHandling(
      `/resumes/${resumeId}/revisions/compare/${revisionId1}/${revisionId2}`
    );

    const result = await handleApiResponse<{
      success: boolean;
      resumeId: string;
      comparison: {
        revision1: {
          revisionId: string;
          data: any;
        };
        revision2: {
          revisionId: string;
          data: any;
        };
      };
    }>(response);

    // Convert both revision data with proper date handling
    return {
      ...result,
      comparison: {
        revision1: {
          ...result.comparison.revision1,
          data: convertFromApi<Types.Resume>(result.comparison.revision1.data, 'Resume'),
        },
        revision2: {
          ...result.comparison.revision2,
          data: convertFromApi<Types.Resume>(result.comparison.revision2.data, 'Resume'),
        },
      },
    };
  }

  /**
   * Bulk operations for resume revisions
   */
  async bulkDeleteResumeRevisions(
    resumeId: string,
    revisionIds: string[]
  ): Promise<{
    success: boolean;
    resumeId: string;
    deletedCount: number;
    failedDeletions: string[];
    message: string;
  }> {
    const response = await this.fetchWithErrorHandling(
      `/resumes/${resumeId}/revisions/bulk-delete`,
      {
        method: 'POST',
        body: JSON.stringify(formatApiRequest({ revisionIds: revisionIds })),
      }
    );

    return handleApiResponse<{
      success: boolean;
      resumeId: string;
      deletedCount: number;
      failedDeletions: string[];
      message: string;
    }>(response);
  }

  /**
   * Get revision differences between two revision IDs
   */
  async getResumeRevisionDiff(
    resumeId: string,
    fromRevisionId: string,
    toRevisionId: string
  ): Promise<{
    success: boolean;
    resumeId: string;
    fromRevisionId: string;
    toRevisionId: string;
    differences: {
      field: string;
      oldValue: any;
      newValue: any;
      changeType: 'added' | 'removed' | 'modified';
    }[];
  }> {
    const response = await this.fetchWithErrorHandling(
      `/resumes/${resumeId}/revisions/diff/${fromRevisionId}/${toRevisionId}`
    );

    return handleApiResponse<{
      success: boolean;
      resumeId: string;
      fromRevisionId: string;
      toRevisionId: string;
      differences: {
        field: string;
        oldValue: any;
        newValue: any;
        changeType: 'added' | 'removed' | 'modified';
      }[];
    }>(response);
  }

  /**
   * Search through resume revision history
   */
  async searchResumeRevisions(
    resumeId: string,
    query: string
  ): Promise<{
    success: boolean;
    resumeId: string;
    query: string;
    matchingRevisions: Array<{
      revisionId: string;
      revisionTimestamp: string;
      matchedFields: string[];
      preview: string;
    }>;
    count: number;
  }> {
    const params = new URLSearchParams({ q: query });
    const response = await this.fetchWithErrorHandling(
      `/resumes/${resumeId}/revisions/search?${params}`
    );

    return handleApiResponse<{
      success: boolean;
      resumeId: string;
      query: string;
      matchingRevisions: Array<{
        revisionId: string;
        revisionTimestamp: string;
        matchedFields: string[];
        preview: string;
      }>;
      count: number;
    }>(response);
  }

  /**
   * Get all historical versions of a resume with full data
   */
  async getResumeHistory(resumeId: string): Promise<{
    success: boolean;
    resumeId: string;
    history: Types.Resume[];
    count: number;
  }> {
    const response = await this.fetchWithErrorHandling(`/resumes/${resumeId}/history`);

    const result = await handleApiResponse<{
      success: boolean;
      resumeId: string;
      history: any[];
      count: number;
    }>(response);

    // Convert all history items with proper date handling
    return {
      ...result,
      history: convertArrayFromApi<Types.Resume>(result.history, 'Resume'),
    };
  }

  /**
   * Get resume revision statistics and metadata
   */
  async getResumeRevisionStatistics(resumeId: string): Promise<{
    success: boolean;
    resumeId: string;
    statistics: {
      totalRevisions: number;
      oldestRevision: string;
      newestRevision: string;
      averageUpdateFrequency: string;
      majorChanges: number;
      minorChanges: number;
    };
  }> {
    const response = await this.fetchWithErrorHandling(`/resumes/${resumeId}/revisions/statistics`);

    return handleApiResponse<{
      success: boolean;
      resumeId: string;
      statistics: {
        totalRevisions: number;
        oldestRevision: string;
        newestRevision: string;
        averageUpdateFrequency: string;
        majorChanges: number;
        minorChanges: number;
      };
    }>(response);
  }

  /**
   * Export resume revision history as JSON or other formats
   */
  async exportResumeHistory(resumeId: string, format: 'json' | 'csv' = 'json'): Promise<Blob> {
    const response = await this.fetchWithErrorHandling(
      `/resumes/${resumeId}/history/export?format=${format}`
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    return response.blob();
  }

  // ============================
  // Resume Revision Utility Methods
  // ============================

  /**
   * Helper method to format revision timestamp for display
   */
  formatRevisionTimestamp(timestamp: string): string {
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZoneName: 'short',
      });
    } catch {
      return timestamp;
    }
  }

  /**
   * Helper method to calculate time between revisions
   */
  calculateTimeBetweenRevisions(timestamp1: string, timestamp2: string): string {
    try {
      const date1 = new Date(timestamp1);
      const date2 = new Date(timestamp2);
      const diffMs = Math.abs(date2.getTime() - date1.getTime());

      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

      if (diffDays > 0) return `${diffDays}d ${diffHours}h`;
      if (diffHours > 0) return `${diffHours}h ${diffMinutes}m`;
      return `${diffMinutes}m`;
    } catch {
      return 'Unknown';
    }
  }

  /**
   * Enhanced error handling with revision-specific errors
   */
  private async handleResumeRevisionResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const errorText = await response.text();

      // Handle specific revision errors
      switch (response.status) {
        case 404:
          if (errorText.includes('revision')) {
            throw new Error(
              'Revision not found - it may have been deleted or the timestamp is invalid'
            );
          }
          throw new Error('Resume not found');
        case 409:
          throw new Error(
            'Revision conflict - the resume may have been updated since this revision'
          );
        case 422:
          throw new Error('Invalid revision data - unable to process the revision');
        default:
          throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
    }

    return handleApiResponse<T>(response);
  }

  async getJob(id: string): Promise<Types.Job> {
    const response = await this.fetchWithErrorHandling(`/jobs/${id}`);

    return this.handleApiResponseWithConversion<Types.Job>(response, 'Job');
  }

  async getJobs(request: Partial<PaginatedRequest> = {}): Promise<PaginatedResponse<Types.Job>> {
    const paginatedRequest = createPaginatedRequest(request);
    const params = toUrlParams(formatApiRequest(paginatedRequest));

    const response = await this.fetchWithErrorHandling(`/jobs?${params}`);

    return this.handlePaginatedApiResponseWithConversion<Types.Job>(response, 'Job');
  }

  async getJobsByEmployer(
    employerId: string,
    request: Partial<PaginatedRequest> = {}
  ): Promise<PaginatedResponse<Types.Job>> {
    const paginatedRequest = createPaginatedRequest(request);
    const params = toUrlParams(formatApiRequest(paginatedRequest));

    const response = await this.fetchWithErrorHandling(`/employers/${employerId}/jobs?${params}`);

    return this.handlePaginatedApiResponseWithConversion<Types.Job>(response, 'Job');
  }

  async searchJobs(
    query: string,
    filters?: Record<string, any>
  ): Promise<PaginatedResponse<Types.Job>> {
    const searchRequest = {
      query,
      filters,
      page: 1,
      limit: 20,
    };

    const params = toUrlParams(formatApiRequest(searchRequest));
    const response = await this.fetchWithErrorHandling(`/jobs/search?${params}`);

    return this.handlePaginatedApiResponseWithConversion<Types.Job>(response, 'Job');
  }

  // ============================
  // Job Application Methods with Date Conversion
  // ============================

  async applyToJob(
    application: Omit<Types.JobApplication, 'id' | 'appliedDate' | 'updatedDate' | 'status'>
  ): Promise<Types.JobApplication> {
    const response = await this.fetchWithErrorHandling('/job-applications', {
      method: 'POST',
      body: JSON.stringify(formatApiRequest(application)),
    });

    return this.handleApiResponseWithConversion<Types.JobApplication>(response, 'JobApplication');
  }

  async getJobApplication(id: string): Promise<Types.JobApplication> {
    const response = await this.fetchWithErrorHandling(`/job-applications/${id}`);

    return this.handleApiResponseWithConversion<Types.JobApplication>(response, 'JobApplication');
  }

  async getJobApplications(
    request: Partial<PaginatedRequest> = {}
  ): Promise<PaginatedResponse<Types.JobApplication>> {
    const paginatedRequest = createPaginatedRequest(request);
    const params = toUrlParams(formatApiRequest(paginatedRequest));

    const response = await this.fetchWithErrorHandling(`/job-applications?${params}`);

    return this.handlePaginatedApiResponseWithConversion<Types.JobApplication>(
      response,
      'JobApplication'
    );
  }

  async updateApplicationStatus(
    id: string,
    status: Types.ApplicationStatus
  ): Promise<Types.JobApplication> {
    const response = await this.fetchWithErrorHandling(`/job-applications/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify(formatApiRequest({ status })),
    });

    return this.handleApiResponseWithConversion<Types.JobApplication>(response, 'JobApplication');
  }

  // ============================
  // Chat Methods with Date Conversion
  // ============================

  /**
   * Create a chat session with optional candidate association
   */
  async createChatSessionWithCandidate(
    request: CreateChatSessionRequest
  ): Promise<Types.ChatSession> {
    const response = await this.fetchWithErrorHandling('/chat/sessions', {
      method: 'POST',
      body: JSON.stringify(formatApiRequest(request)),
    });

    return this.handleApiResponseWithConversion<Types.ChatSession>(response, 'ChatSession');
  }

  /**
   * Get all chat sessions related to a specific candidate
   */
  async getCandidateChatSessions(
    username: string,
    request: Partial<PaginatedRequest> = {}
  ): Promise<CandidateSessionsResponse> {
    const paginatedRequest = createPaginatedRequest(request);
    const params = toUrlParams(formatApiRequest(paginatedRequest));

    const response = await this.fetchWithErrorHandling(
      `/candidates/${username}/chat-sessions?${params}`
    );

    // Handle the nested sessions with date conversion
    const result = await this.handleApiResponseWithConversion<CandidateSessionsResponse>(response);

    // Convert the nested sessions array
    if (result.sessions && result.sessions.data) {
      result.sessions.data = convertArrayFromApi<Types.ChatSession>(
        result.sessions.data,
        'ChatSession'
      );
    }

    return result;
  }

  async getOrCreateChatSession(
    candidate: Types.Candidate,
    title: string,
    context_type: Types.ChatContextType
  ): Promise<Types.ChatSession> {
    const result = await this.getCandidateChatSessions(candidate.username);
    /* Find the 'candidate_chat' session if it exists, otherwise create it */
    let session = result.sessions.data.find(session => session.title === title);
    if (!session) {
      session = await this.createCandidateChatSession(candidate.username, context_type, title);
    }
    return session;
  }

  async getSystemInfo(): Promise<Types.SystemInfo> {
    const response = await this.fetchWithErrorHandling('/system/info');

    const result = await handleApiResponse<Types.SystemInfo>(response);

    return result;
  }

  async getCandidateSimilarContent(query: string): Promise<Types.ChromaDBGetResponse> {
    const response = await this.fetchWithErrorHandling('/candidates/rag-search', {
      method: 'POST',
      body: JSON.stringify(query),
    });

    const result = await handleApiResponse<Types.ChromaDBGetResponse>(response);

    return result;
  }

  getCandidateVectorsStream(
    dimensions: number,
    streamingOptions?: StreamingOptions<Types.ChatMessageRagSearch>
  ): StreamingResponse<Types.ChatMessageRagSearch> {
    const body = JSON.stringify(dimensions);
    return this.streamify<Types.ChatMessageRagSearch>(
      '/candidates/rag-vectors',
      body,
      streamingOptions,
      'ChatMessageRagSearch'
    );
  }

  async getCandidateRAGContent(documentId: string): Promise<Types.RagContentResponse> {
    const response = await this.fetchWithErrorHandling('/candidates/rag-content', {
      method: 'POST',
      body: JSON.stringify({ id: documentId }),
    });

    const result = await handleApiResponse<Types.RagContentResponse>(response);

    return result;
  }

  /**

    uploadCandidateDocument

    usage:
    const controller : StreamingResponse<Types.Document> = uploadCandidateDocument(...);
    const document : Types.Document = await controller.promise;
    console.log(`Document id: ${document.id}`)
   */
  uploadCandidateDocument(
    file: File,
    options: Types.DocumentOptions,
    streamingOptions?: StreamingOptions<Types.DocumentMessage>
  ): StreamingResponse<Types.DocumentMessage> {
    const convertedOptions = toSnakeCase(options);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('filename', file.name);
    formData.append('options', JSON.stringify(convertedOptions));
    streamingOptions = {
      ...streamingOptions,
      headers: {
        // Don't set Content-Type - browser will set it automatically with boundary
        Authorization: this.defaultHeaders['Authorization'],
      },
    };
    return this.streamify<Types.DocumentMessage>(
      '/candidates/documents/upload',
      formData,
      streamingOptions,
      'DocumentMessage'
    );
  }

  createJobFromFile(
    file: File,
    streamingOptions?: StreamingOptions<Types.JobRequirementsMessage>
  ): StreamingResponse<Types.JobRequirementsMessage> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('filename', file.name);
    streamingOptions = {
      ...streamingOptions,
      headers: {
        // Don't set Content-Type - browser will set it automatically with boundary
        Authorization: this.defaultHeaders['Authorization'],
      },
    };
    return this.streamify<Types.JobRequirementsMessage>(
      '/jobs/upload',
      formData,
      streamingOptions,
      'JobRequirementsMessage'
    );
  }

  getJobRequirements(
    jobId: string,
    streamingOptions?: StreamingOptions<Types.DocumentMessage>
  ): StreamingResponse<Types.DocumentMessage> {
    streamingOptions = {
      ...streamingOptions,
      headers: this.defaultHeaders,
    };
    return this.streamify<Types.DocumentMessage>(
      `/jobs/requirements/${jobId}`,
      null,
      streamingOptions,
      'DocumentMessage'
    );
  }

  generateResume(
    candidateId: string,
    jobId: string,
    streamingOptions?: StreamingOptions<Types.ChatMessageResume>
  ): StreamingResponse<Types.ChatMessageResume> {
    streamingOptions = {
      ...streamingOptions,
      headers: this.defaultHeaders,
    };
    return this.streamify<Types.ChatMessageResume>(
      `/candidates/${candidateId}/${jobId}/generate-resume`,
      null,
      streamingOptions,
      'ChatMessageResume'
    );
  }

  candidateMatchForRequirement(
    candidate_id: string,
    requirement: string,
    regenerate: boolean = false,
    streamingOptions?: StreamingOptions<Types.ChatMessageSkillAssessment>
  ): StreamingResponse<Types.ChatMessageSkillAssessment> {
    const body = JSON.stringify(formatApiRequest({ skill: requirement, regenerate }));
    streamingOptions = {
      ...streamingOptions,
      headers: this.defaultHeaders,
    };
    return this.streamify<Types.ChatMessageSkillAssessment>(
      `/candidates/${candidate_id}/skill-match`,
      body,
      streamingOptions,
      'ChatMessageSkillAssessment'
    );
  }

  async updateCandidateDocument(document: Types.Document, content = ''): Promise<Types.Document> {
    const request: Types.DocumentUpdateRequest = {
      filename: document.filename,
      options: document.options,
    };
    if (content) {
      request.content = content;
    }
    const response = await this.fetchWithErrorHandling(`/candidates/documents/${document.id}`, {
      method: 'PATCH',
      body: JSON.stringify(formatApiRequest(request)),
    });

    return await handleApiResponse<Types.Document>(response);
  }

  async deleteCandidateDocument(document: Types.Document): Promise<boolean> {
    const response = await this.fetchWithErrorHandling(`/candidates/documents/${document.id}`, {
      method: 'DELETE',
    });

    const result = await handleApiResponse<boolean>(response);

    return result;
  }

  async getCandidateDocuments(): Promise<Types.DocumentListResponse> {
    const response = await this.fetchWithErrorHandling('/candidates/documents');

    const result = await handleApiResponse<Types.DocumentListResponse>(response);

    return result;
  }

  async getCandidateDocumentText(document: Types.Document): Promise<Types.DocumentContentResponse> {
    const response = await this.fetchWithErrorHandling(
      `/candidates/documents/${document.id}/content`
    );

    const result = await handleApiResponse<Types.DocumentContentResponse>(response);

    return result;
  }

  /**
   * Create a chat session about a specific candidate
   */
  async createCandidateChatSession(
    username: string,
    chatType: Types.ChatContextType = 'candidate_chat',
    title?: string
  ): Promise<Types.ChatSession> {
    const request: CreateChatSessionRequest = {
      username,
      title: title || `Discussion about ${username}`,
      context: {
        type: chatType,
        additionalContext: {},
      },
    };

    return this.createChatSessionWithCandidate(request);
  }

  async createChatSession(context: Types.ChatContext): Promise<Types.ChatSession> {
    const response = await this.fetchWithErrorHandling('/chat/sessions', {
      method: 'POST',
      body: JSON.stringify(formatApiRequest({ context })),
    });

    return this.handleApiResponseWithConversion<Types.ChatSession>(response, 'ChatSession');
  }

  async getChatSession(id: string): Promise<Types.ChatSession> {
    const response = await this.fetchWithErrorHandling(`/chat/sessions/${id}`);

    return this.handleApiResponseWithConversion<Types.ChatSession>(response, 'ChatSession');
  }

  /**
   * Update a chat session's properties
   */
  async updateChatSession(
    id: string,
    updates: UpdateChatSessionRequest
  ): Promise<Types.ChatSession> {
    const response = await this.fetchWithErrorHandling(`/chat/sessions/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(formatApiRequest(updates)),
    });

    return this.handleApiResponseWithConversion<Types.ChatSession>(response, 'ChatSession');
  }

  /**
   * Delete a chat session
   */
  async deleteChatSession(id: string): Promise<{ success: boolean; message: string }> {
    const response = await this.fetchWithErrorHandling(`/chat/sessions/${id}`, {
      method: 'DELETE',
    });

    return handleApiResponse<{ success: boolean; message: string }>(response);
  }

  // ============================
  // Guest Authentication Methods
  // ============================

  /**
   * Create a guest session with authentication
   */
  async createGuestSession(): Promise<Types.AuthResponse> {
    const response = await this.fetchWithErrorHandling('/auth/guest', {
      method: 'POST',
    });

    const result = await handleApiResponse<Types.AuthResponse>(response);

    // Convert guest data if needed
    if (result.user && result.user.userType === 'guest') {
      result.user = convertFromApi<Types.Guest>(result.user, 'Guest');
    }

    return result;
  }

  /**
   * Convert guest account to permanent user account
   */
  async convertGuestToUser(
    registrationData: CreateCandidateRequest & { accountType: 'candidate' }
  ): Promise<{
    message: string;
    auth: Types.AuthResponse;
    conversionType: string;
  }> {
    const response = await this.fetchWithErrorHandling('/auth/guest/convert', {
      method: 'POST',
      body: JSON.stringify(formatApiRequest(registrationData)),
    });

    const result = await handleApiResponse<{
      message: string;
      auth: Types.AuthResponse;
      conversionType: string;
    }>(response);

    // Convert the auth user data
    if (result.auth?.user) {
      result.auth.user = convertFromApi<Types.Candidate>(result.auth.user, 'Candidate');
    }

    return result;
  }

  /**
   * Check if current session is a guest
   */
  isGuestSession(): boolean {
    try {
      const userDataStr = localStorage.getItem(TOKEN_STORAGE.USER_DATA);
      if (userDataStr) {
        const userData = JSON.parse(userDataStr);
        return userData.userType === 'guest';
      }
      return false;
    } catch {
      return false;
    }
  }

  /**
   * Get guest session info
   */
  getGuestSessionInfo(): Types.Guest | null {
    try {
      const userDataStr = localStorage.getItem(TOKEN_STORAGE.USER_DATA);
      if (userDataStr) {
        const userData = JSON.parse(userDataStr);
        if (userData.userType === 'guest') {
          return convertFromApi<Types.Guest>(userData, 'Guest');
        }
      }
      return null;
    } catch {
      return null;
    }
  }

  /**
   * Get rate limit status for current user
   */
  async getRateLimitStatus(): Promise<{
    user_id: string;
    user_type: string;
    is_admin: boolean;
    current_usage: Record<string, number>;
    limits: Record<string, number>;
    remaining: Record<string, number>;
    reset_times: Record<string, string>;
    config: any;
  }> {
    const response = await this.fetchWithErrorHandling('/admin/rate-limits/info');

    return handleApiResponse<any>(response);
  }

  /**
   * Get guest statistics (admin only)
   */
  async getGuestStatistics(): Promise<{
    total_guests: number;
    active_last_hour: number;
    active_last_day: number;
    converted_guests: number;
    by_ip: Record<string, number>;
    creation_timeline: Record<string, number>;
  }> {
    const response = await this.fetchWithErrorHandling('/admin/guests/statistics');

    return handleApiResponse<any>(response);
  }

  /**
   * Cleanup inactive guests (admin only)
   */
  async cleanupInactiveGuests(inactiveHours = 24): Promise<{
    message: string;
    cleaned_count: number;
  }> {
    const response = await this.fetchWithErrorHandling('/admin/guests/cleanup', {
      method: 'POST',
      body: JSON.stringify({ inactive_hours: inactiveHours }),
    });

    return handleApiResponse<{
      message: string;
      cleaned_count: number;
    }>(response);
  }

  // ============================
  // Enhanced Error Handling for Rate Limits
  // ============================

  /**
   * Enhanced API response handler with rate limit handling
   */
  private async handleApiResponseWithRateLimit<T>(response: Response): Promise<T> {
    if (response.status === 429) {
      const rateLimitData = await response.json();
      const retryAfter = response.headers.get('Retry-After');

      throw new RateLimitError(
        rateLimitData.detail?.message || 'Rate limit exceeded',
        parseInt(retryAfter || '60'),
        rateLimitData.detail?.remaining || {}
      );
    }

    return this.handleApiResponseWithConversion<T>(response);
  }

  async resetChatSession(id: string): Promise<{ success: boolean; message: string }> {
    const response = await this.fetchWithErrorHandling(`/chat/sessions/${id}/reset`, {
      method: 'PATCH',
    });

    return handleApiResponse<{ success: boolean; message: string }>(response);
  }

  /**
   * streamify<T = Types.ChatMessage[]>
   * @param api API entrypoint
   * @param data Data to be attached to request Body
   * @param options callbacks, headers, and method
   * @returns
   */
  streamify<T = Types.ChatMessage[]>(
    api: string,
    data: BodyInit | null,
    options: StreamingOptions<T> = {},
    modelType?: string
  ): StreamingResponse<T> {
    const streamOptions: StreamingOptions<T> = {
      ...this.streamingFeedbackCallbacks,
      ...options,
    };
    const abortController = new AbortController();
    const signal = streamOptions.signal || abortController.signal;
    const headers = streamOptions.headers || null;
    const method = streamOptions.method || 'POST';
    const isMultipart = isFormData(data);

    const messageId = '';
    let finalMessage: T | null = null;

    const processStream = async (): Promise<T> => {
      let hasUsableResult = false;
      let unavailableReported = false;
      let slowStartTimer: ReturnType<typeof setTimeout> | null = null;
      const clearSlowStartTimer = (): void => {
        if (slowStartTimer) {
          clearTimeout(slowStartTimer);
          slowStartTimer = null;
        }
      };
      const getSlowStartMessage = (): string => {
        return STREAM_SLOW_START_MESSAGES[
          Math.floor(Math.random() * STREAM_SLOW_START_MESSAGES.length)
        ];
      };
      const scheduleSlowStartTimer = (delayMs: number): void => {
        if (!streamOptions.onSlowStart || hasUsableResult || signal.aborted) {
          return;
        }
        slowStartTimer = setTimeout(() => {
          if (hasUsableResult || signal.aborted) {
            return;
          }
          streamOptions.onSlowStart?.(getSlowStartMessage());
          scheduleSlowStartTimer(STREAM_SLOW_START_REPEAT_MS);
        }, delayMs);
      };
      const reportUnavailable = (): void => {
        if (!unavailableReported && !signal.aborted) {
          unavailableReported = true;
          streamOptions.onUnavailable?.(AI_SERVICE_UNAVAILABLE_MESSAGE);
        }
      };

      scheduleSlowStartTimer(DEFAULT_STREAM_SLOW_START_MS);

      try {
        const normalizedHeaders = this.normalizeHeaders(headers || undefined);
        const requestHeaders = isMultipart
          ? this.stripContentType({
              ...normalizedHeaders,
              ...(this.defaultHeaders['Authorization']
                ? { Authorization: this.defaultHeaders['Authorization'] }
                : {}),
              Accept: 'text/event-stream',
              'Cache-Control': 'no-cache',
            })
          : headers
          ? {
              ...normalizedHeaders,
              ...this.defaultHeaders,
              Accept: 'text/event-stream',
              'Cache-Control': 'no-cache',
            }
          : {
              ...this.defaultHeaders,
              Accept: 'text/event-stream',
              'Cache-Control': 'no-cache',
            };
        const response = await this.fetchWithErrorHandling(api, {
          method,
          headers: requestHeaders,
          body: data,
          signal,
          skipDefaultHeaders: isMultipart,
          suppressErrorCallback: true,
        });

        if (!response.ok) {
          reportUnavailable();
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const reader = response.body?.getReader();
        if (!reader) {
          reportUnavailable();
          throw new Error('Response body is not readable');
        }

        const decoder = new TextDecoder();
        let buffer = '';
        let streamingMessage: Types.ChatMessageStreaming | null = null;
        let thinkingMessage: Types.ChatMessageStatus | null = null;

        try {
          let exit = false;
          while (!exit) {
            const { done, value } = await reader.read();

            if (done) {
              // Stream ended naturally
              exit = true;
              break;
            }

            buffer += decoder.decode(value, { stream: true });

            // Process complete lines
            const lines = buffer.split('\n');
            buffer = lines.pop() || ''; // Keep incomplete line in buffer

            for (const line of lines) {
              if (line.trim() === '') continue; // Skip blank lines between SSEs

              try {
                if (line.startsWith('data: ')) {
                  const data = line.slice(5).trim();
                  const incoming: any = JSON.parse(data);

                  // Handle different status types
                  switch (incoming.status) {
                    case 'streaming':
                      {
                        hasUsableResult = true;
                        clearSlowStartTimer();
                        thinkingMessage = null;
                        const streaming = Types.convertChatMessageStreamingFromApi(incoming);
                        if (streamingMessage === null) {
                          streamingMessage = { ...streaming };
                        } else {
                          // Can't do a simple += as typescript thinks .content might not be there
                          streamingMessage.content =
                            (streamingMessage?.content || '') + streaming.content;
                          // Update timestamp to latest
                          streamingMessage.timestamp = streaming.timestamp;
                        }
                        streamOptions.onStreaming?.(streamingMessage);
                      }
                      break;

                    case 'status':
                      {
                        const status = Types.convertChatMessageStatusFromApi(incoming);
                        if (status.activity === 'thinking') {
                          // Reasoning arrives as incremental deltas - accumulate them
                          // into a single running message so the UI can render one
                          // updating "thinking" bubble instead of one per chunk.
                          const priorContent: string = thinkingMessage?.content || '';
                          const accumulated: Types.ChatMessageStatus = {
                            ...status,
                            content: priorContent + (status.content || ''),
                          };
                          thinkingMessage = accumulated;
                          streamOptions.onStatus?.(accumulated);
                        } else {
                          thinkingMessage = null;
                          streamOptions.onStatus?.(status);
                        }
                      }
                      break;

                    case 'error':
                      {
                        clearSlowStartTimer();
                        thinkingMessage = null;
                        const error = Types.convertChatMessageErrorFromApi(incoming);
                        streamOptions.onError?.(error);
                      }
                      break;

                    case 'done':
                      {
                        hasUsableResult = true;
                        clearSlowStartTimer();
                        thinkingMessage = null;
                        const message = (
                          modelType
                            ? convertFromApi<T>(parseApiResponse<T>(incoming), modelType)
                            : incoming
                        ) as T;
                        finalMessage = message;
                        try {
                          streamOptions.onMessage?.(message);
                        } catch (error) {
                          console.error('onMessage handler failed: ', error);
                        }
                      }
                      break;
                  }
                }
              } catch (error) {
                console.warn('Failed to process SSE:', error);
                if (error instanceof Error) {
                  streamOptions.onWarn?.(error.message);
                }
                if (!hasUsableResult) {
                  reportUnavailable();
                }
              }
            }
          }
        } finally {
          reader.releaseLock();
        }

        clearSlowStartTimer();
        streamOptions.onComplete?.();
        return finalMessage as T;
      } catch (error) {
        clearSlowStartTimer();
        if (signal.aborted) {
          streamOptions.onComplete?.();
          throw new Error('Request was aborted');
        } else {
          console.error(error);
          if (!hasUsableResult) {
            reportUnavailable();
          }
          streamOptions.onError?.({
            sessionId: '',
            status: 'error',
            type: 'text',
            content: (error as Error).message,
          });
          streamOptions.onComplete?.();
          throw error;
        }
      }
    };

    return {
      messageId,
      cancel: () => abortController.abort(),
      promise: processStream(),
    };
  }

  /**
   * Send message with streaming response support and date conversion
   */
  sendMessageStream(
    chatMessage: Types.ChatMessageUser,
    options: StreamingOptions = {}
  ): StreamingResponse {
    const body = JSON.stringify(formatApiRequest(chatMessage));
    return this.streamify(`/chat/sessions/messages/stream`, body, options, 'ChatMessage');
  }

  /**
   * Get persisted chat messages for a session with date conversion
   */
  async getChatMessages(
    sessionId: string,
    request: Partial<PaginatedRequest> = {}
  ): Promise<PaginatedResponse<Types.ChatMessage>> {
    const paginatedRequest = createPaginatedRequest({
      limit: 50, // Higher default for chat messages
      ...request,
    });
    const params = toUrlParams(formatApiRequest(paginatedRequest));

    const response = await this.fetchWithErrorHandling(
      `/chat/sessions/${sessionId}/messages?${params}`
    );

    return this.handlePaginatedApiResponseWithConversion<Types.ChatMessage>(
      response,
      'ChatMessage'
    );
  }

  // ============================
  // Password Reset Methods
  // ============================

  async requestPasswordReset(request: PasswordResetRequest): Promise<{ message: string }> {
    const response = await this.fetchWithErrorHandling('/auth/password-reset/request', {
      method: 'POST',
      body: JSON.stringify(formatApiRequest(request)),
    });

    return handleApiResponse<{ message: string }>(response);
  }

  async confirmPasswordReset(request: PasswordResetConfirm): Promise<{ message: string }> {
    const response = await this.fetchWithErrorHandling('/auth/password-reset/confirm', {
      method: 'POST',
      body: JSON.stringify(formatApiRequest(request)),
    });

    return handleApiResponse<{ message: string }>(response);
  }

  // ============================
  // Password Validation Utilities
  // ============================

  validatePasswordStrength(password: string): {
    isValid: boolean;
    issues: string[];
  } {
    return validatePasswordStrengthRules(password);
  }

  validateEmail(email: string): boolean {
    return validateEmailRule(email);
  }

  validateUsername(username: string): { isValid: boolean; issues: string[] } {
    return validateUsernameRule(username);
  }

  // ============================
  // Error Handling Helper
  // ============================
  async handleRequest<T>(requestFn: () => Promise<Response>, modelType?: string): Promise<T> {
    try {
      const response = await requestFn();
      return await this.handleApiResponseWithConversion<T>(response, modelType);
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  // ============================
  // Utility Methods
  // ============================

  setAuthToken(token: string): void {
    this.defaultHeaders['Authorization'] = `Bearer ${token}`;
  }

  clearAuthToken(): void {
    delete this.defaultHeaders['Authorization'];
  }

  getBaseUrl(): string {
    return this.baseUrl;
  }
}

// ============================
// Request/Response Types
// ============================

export interface CreateCandidateRequest {
  email: string;
  username: string;
  password: string;
  firstName: string;
  lastName: string;
  phone?: string;
}

export interface CreateEmployerRequest {
  email: string;
  username: string;
  password: string;
  companyName: string;
  industry: string;
  companySize: string;
  companyDescription: string;
  websiteUrl?: string;
  phone?: string;
}

export interface EmailVerificationRequest {
  token: string;
}

export interface ResendVerificationRequest {
  email: string;
}

export interface MFARequest {
  email: string;
  password: string;
  deviceId: string;
  deviceName: string;
}

export interface RegistrationResponse {
  message: string;
  email: string;
  verificationRequired: boolean;
}

export interface EmailVerificationResponse {
  message: string;
  accountActivated: boolean;
  userType: string;
}

export interface TrustedDevice {
  deviceId: string;
  deviceName: string;
  browser: string;
  browserVersion: string;
  os: string;
  osVersion: string;
  addedAt: string;
  lastUsed: string;
  ipAddress: string;
}

// ============================
// Additional Types
// ============================

export interface SecurityEvent {
  timestamp: string;
  userId: string;
  eventType:
    | 'login'
    | 'logout'
    | 'mfa_request'
    | 'mfa_verify'
    | 'password_change'
    | 'email_verify'
    | 'device_add'
    | 'device_remove';
  details: {
    ipAddress?: string;
    deviceName?: string;
    success?: boolean;
    failureReason?: string;
    [key: string]: any;
  };
}

export interface PendingVerification {
  id: string;
  email: string;
  userType: 'candidate' | 'employer';
  createdAt: string;
  expiresAt: string;
  attempts: number;
}

export { ApiClient, TOKEN_STORAGE };
export type { StreamingOptions, StreamingResponse };
